import { useState } from "react";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import { Button, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import type {
  MergedPersonRecord,
  MergePersonRequest,
  Person,
  PersonId,
  PersonIdentifier,
  PersonRecord,
  UnmergePersonRequest,
  UpdatePersonRequest,
} from "@thalamos/haven-common";
import { displayName, NHS_NUMBER_SYSTEM } from "@thalamos/haven-common";

import {
  MergeRecordDialog,
  UndoMergeDialog,
  type MergeOutcome,
} from "../../components/MergeDialogs/MergeDialogs.js";
import {
  PersonForm,
  type PersonSaveOutcome,
} from "../../components/PersonForm/PersonForm.js";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

/** Merging records into this one and undoing a merge, where this session may. */
export type PersonMerging = {
  onMerge: (merge: {
    targetPersonId: PersonId;
    request: MergePersonRequest;
  }) => Promise<MergeOutcome>;
  onUndo: (unmerge: {
    targetPersonId: PersonId;
    sourcePersonId: PersonId;
    request: UnmergePersonRequest;
  }) => Promise<MergeOutcome>;
  candidateSearch: {
    value: string;
    onChange: (value: string) => void;
    results: PersonRecord[];
    loading: boolean;
  };
};

const identifierLabel = (t: (key: string) => string, system: string) =>
  system === NHS_NUMBER_SYSTEM ? t("personListing.nhsNumber") : system;

/** An identifier as one line. Read-only wherever it appears: nothing types one. */
const IdentifierLine = ({ identifier }: { identifier: PersonIdentifier }) => {
  const { t } = useTranslation();

  return (
    <Typography variant="body2" color="text.secondary">
      {identifierLabel(t, identifier.system)}: {identifier.value}
    </Typography>
  );
};

const MergedRecordCard = ({
  record,
  own,
  onUndo,
}: {
  record: MergedPersonRecord;
  /**
   * Whether the merged record was registered by the same organisation as the
   * surviving one; otherwise it is flagged as another organisation's.
   */
  own: boolean;
  onUndo: ((record: PersonRecord) => void) | undefined;
}) => {
  const { t } = useTranslation();

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle1" component="h3">
            {displayName(record.names)}
          </Typography>
          <Chip
            size="small"
            label={t("personPage.merges.mergedOn", {
              date: CALENDAR_DATE.format(record.mergedAt),
            })}
          />
          {own ? null : (
            <Chip
              size="small"
              variant="outlined"
              label={t("personPage.merges.createdBy")}
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t("personListing.dateOfBirth")}:{" "}
          {record.dateOfBirth ?? t("personListing.notRecorded")}
        </Typography>
        {record.identifiers.map((identifier) => (
          <IdentifierLine
            key={`${identifier.system}|${identifier.value}`}
            identifier={identifier}
          />
        ))}
        <Typography variant="body2" color="text.secondary">
          {t("personPage.merges.reasonGiven")}: {record.mergeReason}
        </Typography>
        {onUndo === undefined ? null : (
          <Button
            size="small"
            color="warning"
            onClick={() => {
              onUndo(record);
            }}
            sx={{ alignSelf: "flex-start" }}
          >
            {t("personPage.merges.undo")}
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

/**
 * The profile's Identity tab: what the record says about the young person, and
 * which records make them up.
 */
export const IdentityTab = ({
  person,
  merging,
  onSave,
}: {
  person: Person;
  /** Absent unless this session may merge records. */
  merging: PersonMerging | undefined;
  onSave: (
    personId: PersonId,
    request: UpdatePersonRequest,
  ) => Promise<PersonSaveOutcome>;
}) => {
  const { t } = useTranslation();
  const [choosingMerge, setChoosingMerge] = useState(false);
  const [undoing, setUndoing] = useState<PersonRecord | null>(null);

  // The record itself and everything already merged into it are not candidates
  // to merge into it.
  const inTree = new Set([
    person.id,
    ...person.mergedPersonRecords.map((record) => record.id),
  ]);

  return (
    <>
      <PersonForm
        initialValues={{
          dateOfBirth: person.dateOfBirth,
          names: person.names,
        }}
        submitLabel={t("personPage.save")}
        onSave={(request) => onSave(person.id, request)}
      />

      <Paper
        component="section"
        aria-labelledby="home-address-title"
        variant="outlined"
        sx={{ p: 3 }}
      >
        <Stack spacing={1}>
          <Typography variant="h6" component="h2" id="home-address-title">
            {t("personPage.homeAddress.title")}
          </Typography>
          {person.homeAddress === null ? (
            <Typography variant="body2" color="text.secondary">
              {t("personPage.homeAddress.notRecorded")}
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {person.homeAddress.address}
              </Typography>
              <Typography variant="body2">
                {person.homeAddress.postcode}
              </Typography>
            </>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={1}>
          <Typography variant="h6" component="h2">
            {t("personPage.identifiers.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("personPage.identifiers.description")}
          </Typography>
          {person.identifiers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("personPage.identifiers.none")}
            </Typography>
          ) : (
            person.identifiers.map((identifier) => (
              <IdentifierLine
                key={`${identifier.system}|${identifier.value}`}
                identifier={identifier}
              />
            ))
          )}
          {/* A deliberate placeholder — see
              docs/decisions/2026-09-10-defer-pulling-demographics-from-pds-and-partner-systems.md.
              Disabled rather than absent, so the page says where an
              identifier will come from. */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="outlined" size="small" disabled>
              {t("personPage.identifiers.lookUp")}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {t("personPage.identifiers.notAvailable")}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="h6" component="h2">
              {t("personPage.merges.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("personPage.merges.description")}
            </Typography>
          </Stack>

          {person.mergedPersonRecords.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("personPage.merges.none")}
            </Typography>
          ) : (
            person.mergedPersonRecords.map((record) => (
              <MergedRecordCard
                key={record.id}
                record={record}
                own={
                  record.createdByOrganisationId ===
                  person.createdByOrganisationId
                }
                // Only a direct merge is undone here; a record two merges down
                // is undone from the record it was merged into.
                onUndo={
                  merging !== undefined &&
                  record.mergedIntoPersonId === person.id
                    ? setUndoing
                    : undefined
                }
              />
            ))
          )}

          <Divider />

          {merging === undefined ? (
            <Typography variant="body2" color="text.secondary">
              {t("personPage.merges.administratorsOnly")}
            </Typography>
          ) : (
            <Button
              startIcon={<CallMergeIcon />}
              onClick={() => {
                setChoosingMerge(true);
              }}
              sx={{ alignSelf: "flex-start" }}
            >
              {t("personPage.merges.start")}
            </Button>
          )}
        </Stack>
      </Paper>

      {merging === undefined ? null : (
        <>
          <MergeRecordDialog
            open={choosingMerge}
            onClose={() => {
              setChoosingMerge(false);
            }}
            searchValue={merging.candidateSearch.value}
            onSearchChange={merging.candidateSearch.onChange}
            candidates={merging.candidateSearch.results.filter(
              (record) => !inTree.has(record.id),
            )}
            candidatesLoading={merging.candidateSearch.loading}
            onMerge={(request) =>
              merging.onMerge({ targetPersonId: person.id, request })
            }
          />
          <UndoMergeDialog
            record={undoing}
            onClose={() => {
              setUndoing(null);
            }}
            onUndo={(sourcePersonId, request) =>
              merging.onUndo({
                targetPersonId: person.id,
                sourcePersonId,
                request,
              })
            }
          />
        </>
      )}
    </>
  );
};
